/**
 * NotificationBell Component (AC-539)
 *
 * Displays a bell icon with an unread notification count badge.
 * Clicking the bell toggles the NotificationDropdown.
 *
 * Related Tickets:
 * - AC-532 (Epic: In-App Notification System)
 * - AC-539 (NotificationBell component - THIS TICKET)
 */

import React, { useCallback, useEffect, useRef } from 'react';
import { Bell } from 'lucide-react';
import { useNotificationStore } from '../../store/notificationStore';
import { NotificationStore } from '../../types/notification';
import NotificationDropdown from './NotificationDropdown';

// =============================================================================
// Component
// =============================================================================

const NotificationBell: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Get store state and actions - destructure from single call for better test mock compatibility
  const {
    unreadCount,
    isDropdownOpen,
    setDropdownOpen,
    triggerImmediatePoll,
  } = useNotificationStore() as NotificationStore;

  // Format badge text
  const badgeText = unreadCount > 99 ? '99+' : String(unreadCount);

  // Generate aria-label
  const ariaLabel =
    unreadCount > 0
      ? `Notifications - ${unreadCount} unread`
      : 'Notifications - no unread';

  // Handle bell click
  const handleBellClick = useCallback(() => {
    const newState = !isDropdownOpen;
    setDropdownOpen(newState);

    // Trigger immediate poll when opening
    if (newState && triggerImmediatePoll) {
      triggerImmediatePoll();
    }
  }, [isDropdownOpen, setDropdownOpen, triggerImmediatePoll]);

  // Handle keyboard interaction
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleBellClick();
      }
    },
    [handleBellClick]
  );

  // Handle click outside to close dropdown
  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isDropdownOpen, setDropdownOpen]);

  const hasUnread = unreadCount > 0;

  return (
    <div ref={containerRef} className="relative">
      {/* Bell Button */}
      <button
        data-testid="notification-bell"
        className={`
          relative p-2 rounded-full transition-all duration-200
          focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2
          ${hasUnread && !isDropdownOpen
            ? 'text-primary-600 hover:text-primary-700 hover:bg-primary-50'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }
          ${isDropdownOpen ? 'bg-gray-100 dark:bg-gray-700' : ''}
        `}
        onClick={handleBellClick}
        onKeyDown={handleKeyDown}
        aria-label={ariaLabel}
        aria-expanded={isDropdownOpen}
        aria-haspopup="menu"
      >
        <Bell
          className={`w-5 h-5 ${hasUnread && !isDropdownOpen ? 'fill-current' : ''}`}
          strokeWidth={hasUnread && !isDropdownOpen ? 2.5 : 2}
        />

        {/* Unread Badge */}
        {hasUnread && (
          <span
            data-testid="notification-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-xs font-bold text-white bg-red-500 rounded-full shadow-sm animate-pulse"
          >
            {badgeText}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isDropdownOpen && (
        <div className="z-50">
          <NotificationDropdown />
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
